import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Undo2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { BIOMES, drawCell, drawGrid } from "@/lib/biomes";

function Palette({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="biome-palette">
      {BIOMES.map((b) => (
        <button key={b.code} onClick={() => onChange(b.code)} title={b.name} data-testid={`biome-${b.code}`}
          className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs border transition-colors ${value === b.code ? "border-[#D97706] bg-[#1A202C] text-[#F3EBDD]" : "border-[#2D3442] text-[#A8A090] hover:text-[#F3EBDD]"}`}>
          <span className="h-3 w-3 rounded-sm" style={{ background: b.color }} />{b.name}
        </button>
      ))}
    </div>
  );
}

export const MapPainter = ({ map, onSave, onCancel, saving }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const grid = useRef(map.grid.split(""));
  const undo = useRef([]);
  const painting = useRef(false);
  const [cell, setCell] = useState(8);
  const [biome, setBiome] = useState("F");
  const [brush, setBrush] = useState(3);
  const [, force] = useState(0);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    const fit = () => setCell(Math.max(3, Math.floor(Math.min(el.clientWidth / map.cols, el.clientHeight / map.rows))));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [map.cols, map.rows]);

  useEffect(() => {
    drawGrid(canvasRef.current.getContext("2d"), grid.current, map.cols, map.rows, cell);
  }, [cell, map.cols, map.rows]);

  const paintAt = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = Math.floor((e.clientX - rect.left) / cell), cy = Math.floor((e.clientY - rect.top) / cell);
    const ctx = canvasRef.current.getContext("2d");
    const r = brush - 1;
    for (let y = cy - r; y <= cy + r; y++) {
      for (let x = cx - r; x <= cx + r; x++) {
        if (x < 0 || y < 0 || x >= map.cols || y >= map.rows || (x - cx) ** 2 + (y - cy) ** 2 > r * r + r) continue;
        const i = y * map.cols + x;
        if (grid.current[i] === biome) continue;
        grid.current[i] = biome;
        drawCell(ctx, grid.current, map.cols, i, cell);
      }
    }
  }, [cell, brush, biome, map.cols, map.rows]);

  const down = (e) => {
    undo.current = [...undo.current.slice(-24), grid.current.join("")];
    painting.current = true;
    paintAt(e);
    force((n) => n + 1);
  };
  const doUndo = () => {
    const prev = undo.current.pop();
    if (!prev) return;
    grid.current = prev.split("");
    drawGrid(canvasRef.current.getContext("2d"), grid.current, map.cols, map.rows, cell);
    force((n) => n + 1);
  };

  return (
    <div className="flex h-full flex-col" data-testid="map-painter">
      <div className="flex flex-wrap items-center gap-4 border-b border-[#2D3442] bg-[#13171F] px-5 py-3">
        <Palette value={biome} onChange={setBiome} />
        <div className="flex items-center gap-2 w-44 text-xs text-[#A8A090]">Brush
          <Slider min={1} max={8} step={1} value={[brush]} onValueChange={([v]) => setBrush(v)} data-testid="brush-size-slider" />
          <span className="font-mono-lf w-4">{brush}</span>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={doUndo} disabled={!undo.current.length} data-testid="paint-undo-button"><Undo2 className="h-4 w-4 mr-1" />Undo</Button>
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="paint-cancel-button"><X className="h-4 w-4 mr-1" />Cancel</Button>
          <Button size="sm" onClick={() => onSave(grid.current.join(""))} disabled={saving} data-testid="paint-save-button"><Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save map"}</Button>
        </div>
      </div>
      <div ref={wrapRef} className="flex-1 flex items-center justify-center overflow-hidden p-4 bg-[#0F131A]">
        <canvas ref={canvasRef} width={map.cols * cell} height={map.rows * cell} className="cursor-crosshair rounded shadow-[0_10px_40px_rgba(0,0,0,.5)] touch-none"
          onPointerDown={down} onPointerMove={(e) => painting.current && paintAt(e)} onPointerUp={() => (painting.current = false)} onPointerLeave={() => (painting.current = false)}
          data-testid="paint-canvas" />
      </div>
    </div>
  );
};
